import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Controls/Input';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect, useRef } from 'react';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { setCompanyRequiredFields } from '@/utils/required-fields/company.utils';
import { useGetExistingCompany } from '@/hooks/company.hooks';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';

export const CompanyInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const emailFieldRef = useRef<HTMLDivElement>(null);

  /**
   * === Set Value Watchers section ===
   */
  const nameValue = watch && watch('name' as Path<TSchema>);

  const businessIdNumberValue =
    watch && watch('businessIdNumber' as Path<TSchema>);

  const emailValue = watch && watch('email' as Path<TSchema>);

  const activeValue = watch && watch('active' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: existingCompany } = useGetExistingCompany({
    id: data?.id,
    name: nameValue,
    businessIdNumber: businessIdNumberValue,
    email: emailValue,
  });

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (nameValue) {
      resetFieldError<TSchema>({ fieldName: 'name', setError });
    }

    if (businessIdNumberValue) {
      resetFieldError<TSchema>({
        fieldName: 'businessIdNumber',
        setError,
      });
    }

    if (emailValue) {
      resetFieldError<TSchema>({ fieldName: 'email', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue, businessIdNumberValue, emailValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setCompanyRequiredFields({ setValue, data });
      setValue('logo' as Path<TSchema>, data.logo ?? '');
      setValue('addressLine2' as Path<TSchema>, data.addressLine1 ?? '');
      setValue('state' as Path<TSchema>, data.state ?? '');
      setValue('active' as Path<TSchema>, data.active);
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const companyErrorMessages = existingCompany?.data?.errorMessages;

    if (companyErrorMessages?.includes(FieldError.NAME_EXISTS)) {
      setError('name' as Path<TSchema>, {
        message: t('common:companyNameAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (companyErrorMessages?.includes(FieldError.BUSINESS_ID_NUMBER_EXISTS)) {
      setError('businessIdNumber' as Path<TSchema>, {
        message: t('common:companyBusinessIdNumberAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (companyErrorMessages?.includes(FieldError.EMAIL_EXISTS)) {
      setError('email' as Path<TSchema>, {
        message: t('common:companyEmailAlreadyExists'),
      });
      emailFieldRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={
          <TitleSection
            text={t('theCompany')}
            defaultIconClassName="ki-badge"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <FormField
            text={t('common:logo')}
            containerClassName="mb-2"
            textWrapperClassName="mt-3"
          >
            <Avatar
              imageUrl={data?.logo}
              onChange={(url) =>
                setValue?.(
                  'logo' as Path<TSchema>,
                  url as PathValue<TSchema, Path<TSchema>>
                )
              }
            />
          </FormField>
          <FormField
            text={t('common:companyName')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('name' as Path<TSchema>))}
                errorMessage={errors && (errors.name?.message as string)}
                placeholder={t('common:companyName')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:companyIdNumber')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('businessIdNumber' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.businessIdNumber?.message as string)
                }
                placeholder={t('common:companyIdNumber')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:addressLine1')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('addressLine1' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.addressLine1?.message as string)
                }
                placeholder={t('common:addressLine1')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:addressLine2')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('addressLine2' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.addressLine2?.message as string)
                }
                placeholder={t('common:addressLine2')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:postalCode')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('postalCode' as Path<TSchema>))}
                errorMessage={errors && (errors.postalCode?.message as string)}
                placeholder={t('common:postalCode')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:city')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('city' as Path<TSchema>))}
                errorMessage={errors && (errors.city?.message as string)}
                placeholder={t('common:city')}
                type="search"
              />
            </div>
          </FormField>
          <FormField text={t('common:state')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('state' as Path<TSchema>))}
                errorMessage={errors && (errors.state?.message as string)}
                placeholder={t('common:state')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:country')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('country' as Path<TSchema>))}
                errorMessage={errors && (errors.country?.message as string)}
                placeholder={t('common:country')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:phoneNumber')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('phone' as Path<TSchema>))}
                errorMessage={errors && (errors.phone?.message as string)}
                placeholder={t('common:phoneNumber')}
                type="tel"
              />
            </div>
          </FormField>
          <FormField
            containerRef={emailFieldRef}
            text={t('common:email')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('email' as Path<TSchema>))}
                errorMessage={errors && (errors.email?.message as string)}
                placeholder={t('common:email')}
                type="email"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:activeStatus')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
              <Switch
                {...(register && register('active' as Path<TSchema>))}
                errorMessage={errors && (errors.active?.message as string)}
                checked={data ? activeValue : true}
              />
            </div>
          </FormField>
        </>
      </Card>
    </form>
  );
};
